import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import SelectInput from "@commons/Input/Select";
import { usePage } from "@hooks/usePage";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import { ROLES, ROLE_OPTIONS } from "@typings/entities/users/enums";
import { Checkbox, FormControlLabel } from "@mui/material";
import type { UserItemList } from "@services/users/list/type";
import { createUser } from "@services/users/create";
import { updateUser } from "@services/users/update";
import { useUser } from "@hooks/useUser";

const createUserSchema = z
  .object({
    username: z.string().min(1, "Nome é obrigatório"),
    email: z.string().min(1, "E-mail é obrigatório").email("E-mail inválido"),
    role: z.enum(
      [ROLES.ADMIN, ROLES.PMMA, ROLES.SEFAC, ROLES.IBAMA, ROLES.LAPAS],
      {
        message: "Tipo de acesso é obrigatório",
      },
    ),
    changePassword: z.boolean(),
    password: z
      .string()
      .min(8, "Senha deve ter no mínimo 8 caracteres")
      .regex(/[A-Z]/, "Senha deve conter pelo menos uma letra maiúscula")
      .regex(/[a-z]/, "Senha deve conter pelo menos uma letra minúscula")
      .regex(/[0-9]/, "Senha deve conter pelo menos um número"),
    confirmPassword: z.string().min(1, "Confirme a senha"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "As senhas não coincidem",
    path: ["confirmPassword"],
  });

const editUserSchema = z
  .object({
    username: z.string().min(1, "Nome é obrigatório"),
    email: z.string().min(1, "E-mail é obrigatório").email("E-mail inválido"),
    role: z.enum(
      [ROLES.ADMIN, ROLES.PMMA, ROLES.SEFAC, ROLES.IBAMA, ROLES.LAPAS],
      {
        message: "Tipo de acesso é obrigatório",
      },
    ),
    changePassword: z.boolean(),
    password: z.string().optional(),
    confirmPassword: z.string().optional(),
  })
  .refine(
    (data) => {
      if (!data.changePassword) return true;
      return !!data.password && data.password.length >= 8;
    },
    {
      message: "Senha deve ter no mínimo 8 caracteres",
      path: ["password"],
    },
  )
  .refine(
    (data) => {
      if (!data.changePassword) return true;
      return !!data.password && /[A-Z]/.test(data.password);
    },
    {
      message: "Senha deve conter pelo menos uma letra maiúscula",
      path: ["password"],
    },
  )
  .refine(
    (data) => {
      if (!data.changePassword) return true;
      return !!data.password && /[a-z]/.test(data.password);
    },
    {
      message: "Senha deve conter pelo menos uma letra minúscula",
      path: ["password"],
    },
  )
  .refine(
    (data) => {
      if (!data.changePassword) return true;
      return !!data.password && /[0-9]/.test(data.password);
    },
    {
      message: "Senha deve conter pelo menos um número",
      path: ["password"],
    },
  )
  .refine(
    (data) => {
      if (!data.changePassword) return true;
      return data.password === data.confirmPassword;
    },
    {
      message: "As senhas não coincidem",
      path: ["confirmPassword"],
    },
  );

type CreateUserFormData = z.infer<typeof createUserSchema>;
type EditUserFormData = z.infer<typeof editUserSchema>;
type UserFormData = CreateUserFormData | EditUserFormData;

type ModalUserFormProps = {
  user?: UserItemList | null;
  onSuccess: () => void;
};

export default function ModalUserForm({ user, onSuccess }: ModalUserFormProps) {
  const { isModalOpen, closeModal, setIsLoading } = usePage();
  const { user: currentUser, saveUser } = useUser();
  const isEditMode = !!user;
  const modalName = isEditMode ? "editUser" : "newUser";
  const open = isModalOpen(modalName);

  const defaultValues = user
    ? {
        username: user.username,
        email: user.email,
        role: user.role,
        changePassword: false,
        password: "",
        confirmPassword: "",
      }
    : {
        username: "",
        email: "",
        role: "" as (typeof ROLES)[keyof typeof ROLES],
        changePassword: false,
        password: "",
        confirmPassword: "",
      };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<UserFormData>({
    resolver: zodResolver(isEditMode ? editUserSchema : createUserSchema),
    values: defaultValues,
  });

  const changePasswordValue = watch("changePassword");
  const onSubmit = async (data: UserFormData) => {
    try {
      setIsLoading(true);

      if (isEditMode) {
        const updateData: {
          username?: string;
          password?: string;
          role?: (typeof ROLES)[keyof typeof ROLES];
        } = {
          username: data.username,
          role: data.role,
        };

        if (data.changePassword && data.password) {
          updateData.password = data.password;
        }

        await updateUser(user.id, updateData);

        if (currentUser?.id === user.id) {
          const updatedUser = {
            ...currentUser,
            username: data.username,
            role: data.role,
          };
          saveUser(updatedUser);
        }

        toast.success(`Usuário ${data.username} atualizado com sucesso`);
      } else {
        await createUser({
          username: data.username,
          email: data.email,
          password: data.password!,
          role: data.role,
        });
        toast.success(`Usuário ${data.username} criado com sucesso`);
      }

      onSuccess();
      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError
        ? error.message
        : `Erro ao ${isEditMode ? "atualizar" : "criar"} usuário`;

      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    closeModal();
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      heading={isEditMode ? "Editar Usuário" : "Cadastro de novo usuário"}
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-lg"
    >
      <form className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-4">
        <div className="md:col-span-2">
          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Nome"
                textSize="sm"
                fullWidth
                error={!!errors.username}
                helperText={errors.username?.message}
              />
            )}
          />
        </div>

        <div className="md:col-span-2">
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <SelectInput
                {...field}
                label="Tipo de Acesso"
                options={ROLE_OPTIONS}
                size="small"
                fullWidth
                error={!!errors.role}
                helperText={errors.role?.message}
              />
            )}
          />
        </div>

        <div className="md:col-span-2">
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Email"
                type="email"
                textSize="sm"
                fullWidth
                disabled={isEditMode}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />
        </div>

        {isEditMode && (
          <div className="md:col-span-2">
            <Controller
              name="changePassword"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Alterar senha"
                />
              )}
            />
          </div>
        )}

        {(!isEditMode || changePasswordValue) && (
          <>
            <div className="md:col-span-2">
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Senha"
                    type="password"
                    textSize="sm"
                    fullWidth
                    error={!!errors.password}
                    helperText={errors.password?.message}
                  />
                )}
              />
            </div>

            <div className="md:col-span-2">
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Confirmar Senha"
                    type="password"
                    textSize="sm"
                    fullWidth
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                  />
                )}
              />
            </div>
          </>
        )}
      </form>
    </Modal>
  );
}
